"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { ComponentType, ReactNode } from "react";
import { ArrowRightIcon, BedIcon, BotIcon, CalendarIcon, CameraIcon, CheckIcon, CompassIcon, ImageIcon, MapPinIcon, ReceiptIcon, RouteIcon, SearchIcon, SparklesIcon, SplitIcon, UploadIcon, UsersIcon, VoteIcon, WalletIcon, type LandingIconProps } from "./LandingIcons";
const glass = "border border-border bg-surface/75 shadow-card backdrop-blur-xl";
const glassStrong = "border border-border bg-surface/90 shadow-card-hover backdrop-blur-2xl";
type IconComponent = ComponentType<LandingIconProps>;
function Eyebrow(t0) {
  const $ = _c(5);
  const {
    icon: Icon,
    children
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="h-4 w-4" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="inline-flex items-center gap-2 rounded-full border border-border-brand/50 bg-brand-primary-soft px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-brand-primary sm:text-[11px]">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function CheckRow(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border border-border-brand/40 bg-brand-primary-soft text-brand-primary"><CheckIcon className="h-3.5 w-3.5" /></span>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="flex items-start gap-3 text-sm leading-6 text-text-primary/[0.64]">{t1}<span>{children}</span></div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
type InitialTone = "strong" | "soft" | "muted" | "outline";
type InitialItem = {
  tone: InitialTone;
};
const GROUP_TOOLS = [{
  icon: SparklesIcon,
  title: "AI gameplans",
  copy: "Turn the group chat into an itinerary.",
  tone: "bg-brand-primary-soft text-brand-primary"
}, {
  icon: WalletIcon,
  title: "Shared finances",
  copy: "Know who paid, owes, and settled.",
  tone: "bg-success-soft text-success-text"
}, {
  icon: ReceiptIcon,
  title: "Receipt analysis",
  copy: "Scan totals and split actual items.",
  tone: "bg-warning-soft text-warning-text"
}, {
  icon: CameraIcon,
  title: "One media vault",
  copy: "Keep the trip album with the group.",
  tone: "bg-brand-secondary-soft text-brand-secondary"
}] satisfies ReadonlyArray<{
  icon: IconComponent;
  title: string;
  copy: string;
  tone: string;
}>;
const PLANNER_DAYS = [{
  day: "Day 1",
  title: "Arrival + Shibuya",
  time: "4 stops · relaxed",
  tone: "strong"
}, {
  day: "Day 2",
  title: "Tsukiji + teamLab",
  time: "6 stops · full day",
  tone: "soft"
}, {
  day: "Day 3",
  title: "Hakone reset",
  time: "3 stops · day trip",
  tone: "muted"
}] satisfies ReadonlyArray<InitialItem & {
  day: string;
  title: string;
  time: string;
}>;
const STAY_OPTIONS = [{
  title: "Shibuya sky loft",
  price: "$189",
  score: "6 votes",
  theme: "primary"
}, {
  title: "Shinjuku townhouse",
  price: "$164",
  score: "4 votes",
  theme: "secondary"
}, {
  title: "Asakusa river stay",
  price: "$142",
  score: "2 votes",
  theme: "accent"
}] as const;
const MONEY_ACTIVITY = [{
  who: "Marcus",
  item: "Airbnb deposit",
  amount: "+$612.00",
  tone: "soft"
}, {
  who: "Jordan",
  item: "Baseball tickets",
  amount: "+$186.40",
  tone: "muted"
}, {
  who: "You",
  item: "Airport transfer",
  amount: "+$94.50",
  tone: "strong"
}] satisfies ReadonlyArray<InitialItem & {
  who: string;
  item: string;
  amount: string;
}>;
const RECEIPT_SPLITS = [{
  name: "You",
  state: "Ramen + drinks",
  amount: "$36.00",
  tone: "strong"
}, {
  name: "Marcus",
  state: "Ramen + gyoza",
  amount: "$31.50",
  tone: "soft"
}, {
  name: "Jordan",
  state: "Ramen + drinks",
  amount: "$34.00",
  tone: "muted"
}] satisfies ReadonlyArray<InitialItem & {
  name: string;
  state: string;
  amount: string;
}>;
const MEDIA_TILES = [{
  span: "col-span-2 row-span-2",
  style: "from-trip-nightlife via-brand-secondary to-brand-primary",
  label: "Shibuya arrival"
}, {
  span: "",
  style: "from-trip-stay via-brand-secondary to-brand-accent",
  label: ""
}, {
  span: "",
  style: "from-trip-food via-warning to-brand-accent",
  label: ""
}, {
  span: "row-span-2",
  style: "from-trip-transit via-info to-brand-primary",
  label: ""
}, {
  span: "",
  style: "from-trip-activity via-success to-brand-primary",
  label: ""
}, {
  span: "",
  style: "from-brand-accent via-trip-nightlife to-brand-secondary",
  label: ""
}] as const;
function StayPreview(t0) {
  const $ = _c(10);
  const {
    theme
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      primary: "from-brand-primary via-trip-flight to-brand-secondary",
      secondary: "from-brand-secondary via-trip-stay to-brand-accent",
      accent: "from-brand-accent via-trip-food to-warning"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const styles = t1 satisfies Record<(typeof STAY_OPTIONS)[number]["theme"], string>;
  const t2 = `absolute inset-0 bg-gradient-to-br ${styles[theme]}`;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute -right-5 -top-7 size-24 rounded-full bg-text-on-brand/20 blur-sm" />;
    t4 = <div className="absolute inset-x-0 bottom-0 h-[58%] bg-gradient-to-t from-bg/80 to-transparent" />;
    t5 = <div className="absolute bottom-0 left-[8%] h-[48%] w-[18%] rounded-t-md bg-bg/75" />;
    t6 = <div className="absolute bottom-0 left-[29%] h-[66%] w-[22%] rounded-t-lg bg-bg/70" />;
    t7 = <div className="absolute bottom-0 left-[54%] h-[42%] w-[14%] rounded-t-md bg-bg/70" />;
    t8 = <div className="absolute bottom-0 right-[8%] h-[58%] w-[19%] rounded-t-lg bg-bg/75" />;
    t9 = <div className="absolute bottom-[25%] left-[34%] h-1.5 w-1.5 rounded-full bg-brand-accent shadow-brand-glow" />;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
    $[6] = t8;
    $[7] = t9;
  } else {
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
    t6 = $[4];
    t7 = $[5];
    t8 = $[6];
    t9 = $[7];
  }
  let t10;
  if ($[8] !== t2) {
    t10 = <div className={t2}>{t3}{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[8] = t2;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  return t10;
}
function Initial(t0) {
  const $ = _c(4);
  const {
    children,
    tone: t1
  } = t0;
  const tone = t1 === undefined ? "strong" : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      strong: "bg-brand-primary text-text-on-brand",
      soft: "bg-brand-secondary-soft text-brand-secondary",
      muted: "bg-brand-accent-soft text-brand-accent",
      outline: "border border-border bg-surface text-text-secondary"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const classNames = t2 satisfies Record<InitialTone, string>;
  const t3 = `grid size-9 shrink-0 place-items-center rounded-full text-xs font-black ${classNames[tone]}`;
  let t4;
  if ($[1] !== children || $[2] !== t3) {
    t4 = <span className={t3}>{children}</span>;
    $[1] = children;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}
export function GuestOverview() {
  const $ = _c(15);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Eyebrow icon={UsersIcon}>One home for the whole crew</Eyebrow>;
    t1 = <h2 className="mt-6 max-w-xl text-3xl font-black tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-6xl">Create the group. Make the trip real.</h2>;
    t2 = <p className="mt-5 max-w-xl text-base leading-7 text-text-primary/[0.56] sm:text-lg">Every group gets a shared command center for destinations, votes, plans, stays, balances, receipts, and the memories afterward.</p>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div>{t0}{t1}{t2}<div className="mt-8 grid gap-3 sm:grid-cols-2">{GROUP_TOOLS.map(_temp)}</div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,var(--color-brand-primary),transparent_38%)] opacity-[0.16]" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div><p className="text-[10px] font-black uppercase tracking-[0.18em] text-text-primary/45">Active group</p><h3 className="mt-1 text-2xl font-black tracking-[-0.035em] text-text-primary">Tokyo Drift</h3></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex items-center justify-between gap-4 border-b border-border pb-4">{t5}<div className="flex -space-x-2">{["L", "M", "J", "+4"].map(_temp2)}</div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  let t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-start justify-between"><span className="rounded-full border border-border bg-brand-primary px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-text-on-brand">Planning</span><MapPinIcon className="h-5 w-5 text-brand-primary" /></div>;
    t8 = <p className="mt-16 text-[10px] font-black uppercase tracking-[0.16em] text-text-primary/[0.35]">Destination</p>;
    t9 = <p className="mt-1 text-2xl font-black text-text-primary">Tokyo, Japan</p>;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t7 = $[7];
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="rounded-3xl border border-border-brand/40 bg-brand-primary-soft/70 p-5">{t7}{t8}{t9}<div className="mt-4 flex items-center gap-2 text-xs font-bold text-text-primary/[0.48]"><CalendarIcon className="h-4 w-4" />Oct 8–16</div></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex items-center justify-between"><span className="text-[10px] font-black uppercase tracking-[0.16em] text-text-primary/[0.36]">Group vote</span><VoteIcon className="h-4 w-4 text-text-primary/65" /></div>;
    t12 = <p className="mt-3 text-sm font-black text-text-primary">Shinjuku or Shibuya?</p>;
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="rounded-3xl border border-border bg-surface/55 p-4">{t11}{t12}<div className="mt-4 h-2 overflow-hidden rounded-full bg-brand-secondary-soft"><div className="h-full w-[68%] rounded-full bg-brand-secondary" /></div><p className="mt-2 text-[10px] text-text-primary/[0.38]">5 of 7 voted</p></div>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="grid items-center gap-9 lg:grid-cols-[0.88fr_1.12fr] lg:gap-16">{t3}<div className={`${glassStrong} relative overflow-hidden rounded-[34px] p-4 sm:p-5`}>{t4}<div className="relative rounded-[26px] border border-border bg-surface/85 p-4 sm:p-6">{t6}<div className="mt-4 grid gap-3 sm:grid-cols-[1.15fr_0.85fr]">{t10}<div className="space-y-3">{t13}<div className="rounded-3xl border border-border bg-surface/55 p-4"><div className="flex items-center justify-between"><span className="text-[10px] font-black uppercase tracking-[0.16em] text-text-primary/[0.36]">Next up</span><RouteIcon className="h-4 w-4 text-text-primary/70" /></div><p className="mt-3 text-sm font-black text-text-primary">Lock the gameplan</p><p className="mt-1 text-xs text-text-primary/[0.42]">AI draft ready to review</p></div></div></div></div></div></div>;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  return t14;
}
function _temp2(label, index) {
  return <span key={label} className={`grid h-9 w-9 place-items-center rounded-full border-2 border-bg text-[10px] font-black text-text-primary ${index === 0 ? "bg-brand-primary-soft text-brand-primary" : index === 1 ? "bg-brand-secondary-soft text-brand-secondary" : index === 2 ? "bg-brand-accent-soft text-brand-accent" : "bg-surface"}`}>{label}</span>;
}
function _temp(t0) {
  const {
    icon: Icon,
    title,
    copy,
    tone
  } = t0;
  return <div key={title} className="rounded-2xl border border-border bg-surface/70 p-4 shadow-card backdrop-blur-xl"><span className={`grid size-9 place-items-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></span><h3 className="mt-3 text-sm font-black text-text-primary">{title}</h3><p className="mt-1 text-xs leading-5 text-text-primary/[0.44]">{copy}</p></div>;
}
export function PlannerShowcase() {
  const $ = _c(10);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Eyebrow icon={SparklesIcon}>AI trip planning</Eyebrow>;
    t1 = <h2 className="mt-6 max-w-xl text-3xl font-black tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-6xl">Give AI the chaos. Get back a gameplan.</h2>;
    t2 = <p className="mt-5 max-w-xl text-base leading-7 text-text-primary/[0.56] sm:text-lg">Feed it the destination, dates, budget, pace, interests, and non-negotiables. Boy Trips builds a practical itinerary the group can edit together.</p>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div>{t0}{t1}{t2}<div className="mt-8 space-y-3"><CheckRow>Generate complete day-by-day plans from a short prompt</CheckRow><CheckRow>Rebalance busy days, bad weather, or a changed budget</CheckRow><CheckRow>Keep bookings, notes, and decisions attached to each stop</CheckRow></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-primary-soft text-brand-primary"><BotIcon className="h-6 w-6" /></span>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center gap-3 border-b border-border pb-4">{t4}<div className="min-w-0 flex-1"><p className="text-sm font-black text-text-primary">Gameplan generator</p><p className="truncate text-xs text-text-primary/[0.38]">Tokyo · 7 travelers · $2,400 each</p></div><span className="rounded-full border border-border bg-brand-primary px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-text-on-brand">Ready</span></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mt-4 rounded-2xl border border-border-brand/35 bg-brand-primary-soft/55 p-4"><p className="text-xs leading-5 text-text-primary/[0.58]">“Build a food-heavy trip with nightlife, one reset day, no mornings before 9, and room for a baseball game.”</p></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="mt-4 space-y-3">{PLANNER_DAYS.map(_temp3)}</div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <button type="button" className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-brand-primary px-4 py-3 text-xs font-black text-text-on-brand transition hover:bg-brand-primary-hover"><SparklesIcon className="h-4 w-4" />Regenerate</button>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="grid items-center gap-9 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">{t3}<div className={`${glassStrong} overflow-hidden rounded-[34px] p-4 sm:p-5`}><div className="rounded-[26px] border border-border bg-bg/85 p-4 sm:p-6">{t5}{t6}{t7}<div className="mt-4 flex gap-3">{t8}<button type="button" className="grid h-11 w-11 place-items-center rounded-2xl border border-border bg-surface/70 text-text-primary"><CompassIcon className="h-5 w-5" /></button></div></div></div></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function _temp3(item, index) {
  return <div key={item.day} className="flex items-center gap-3 rounded-2xl border border-border bg-surface/45 p-3.5"><Initial tone={item.tone}>{index + 1}</Initial><div className="min-w-0 flex-1"><p className="text-[10px] font-black uppercase tracking-[0.15em] text-text-primary/[0.34]">{item.day}</p><p className="truncate text-sm font-black text-text-primary/[0.88]">{item.title}</p></div><span className="text-[10px] font-bold text-text-primary/[0.36]">{item.time}</span></div>;
}
export function StayShowcase() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-3 border-b border-border pb-4 sm:flex-row sm:items-center"><div className="flex flex-1 items-center gap-3 rounded-2xl border border-border bg-surface/60 px-4 py-3 text-sm text-text-primary/[0.52]"><SearchIcon className="h-5 w-5" />Tokyo · 7 guests · Oct 8–16</div><button type="button" className="rounded-2xl bg-brand-primary px-5 py-3 text-xs font-black text-text-on-brand transition hover:bg-brand-primary-hover">Search stays</button></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className={`${glassStrong} order-2 overflow-hidden rounded-[34px] p-4 sm:p-5 lg:order-1`}><div className="rounded-[26px] border border-border bg-bg/85 p-4 sm:p-6">{t0}<div className="mt-4 grid gap-3 sm:grid-cols-3">{STAY_OPTIONS.map(_temp4)}</div></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Eyebrow icon={BedIcon}>Find and decide together</Eyebrow>;
    t3 = <h2 className="mt-6 max-w-xl text-3xl font-black tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-6xl">Stop losing stays inside the group chat.</h2>;
    t4 = <p className="mt-5 max-w-xl text-base leading-7 text-text-primary/[0.56] sm:text-lg">Pull promising rentals into one shortlist, compare the real per-person cost, and let the crew vote before the good option disappears.</p>;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="grid items-center gap-9 lg:grid-cols-[1.12fr_0.88fr] lg:gap-16">{t1}<div className="order-1 lg:order-2">{t2}{t3}{t4}<div className="mt-8 space-y-3"><CheckRow>Compare location, beds, amenities, and nightly cost</CheckRow><CheckRow>Vote without forwarding the same link fifteen times</CheckRow><CheckRow>Keep the final booking attached to the itinerary</CheckRow></div></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function _temp4(stay, index) {
  return <div key={stay.title} className="overflow-hidden rounded-3xl border border-border bg-surface/50"><div className="relative h-32 overflow-hidden"><StayPreview theme={stay.theme} /><span className="absolute left-3 top-3 rounded-full border border-border bg-bg/65 px-2 py-1 text-[9px] font-black uppercase tracking-[0.13em] text-text-primary backdrop-blur-md">#{index + 1}</span><BedIcon className="absolute bottom-3 right-3 h-5 w-5 text-text-on-brand drop-shadow" /></div><div className="p-3.5"><p className="text-sm font-black text-text-primary">{stay.title}</p><div className="mt-3 flex items-center justify-between text-[10px]"><span className="font-bold text-text-primary/[0.42]">{stay.score}</span><strong className="text-text-primary">{stay.price}<span className="font-medium text-text-primary/[0.35]"> / night</span></strong></div><div className="mt-3 h-1.5 overflow-hidden rounded-full bg-brand-primary-soft"><div className={`h-full rounded-full bg-brand-primary ${index === 0 ? "w-[86%]" : index === 1 ? "w-[58%]" : "w-[31%]"}`} /></div></div></div>;
}
export function MoneyShowcase() {
  const $ = _c(13);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Eyebrow icon={WalletIcon}>Trip finances</Eyebrow>;
    t1 = <h2 className="mt-6 max-w-xl text-3xl font-black tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-6xl">Every dollar has an owner and a reason.</h2>;
    t2 = <p className="mt-5 max-w-xl text-base leading-7 text-text-primary/[0.56] sm:text-lg">Track shared expenses as they happen, choose who participated, and keep a live settlement view without maintaining a side spreadsheet.</p>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div>{t0}{t1}{t2}<div className="mt-8 space-y-3"><CheckRow>Split equally, by shares, or with custom amounts</CheckRow><CheckRow>See individual and group balances in real time</CheckRow><CheckRow>Settle up without rebuilding the trip history</CheckRow></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-[10px] font-black uppercase tracking-[0.18em] text-text-primary/[0.36]">Your balance</p>;
    t5 = <p className="mt-3 text-4xl font-black tracking-[-0.06em] text-success-text">+$186.40</p>;
    t6 = <p className="mt-2 text-xs text-text-primary/[0.4]">You are owed across 4 expenses</p>;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center justify-between text-xs"><span className="text-text-primary/[0.4]">Trip spend</span><strong className="text-text-primary">$4,284</strong></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex items-center justify-between text-xs"><span className="text-text-primary/[0.4]">Your share</span><strong className="text-text-primary">$612</strong></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="rounded-[26px] border border-success/25 bg-success-soft/70 p-5">{t4}{t5}{t6}<div className="mt-7 space-y-3 border-t border-border pt-5">{t7}{t8}<div className="flex items-center justify-between text-xs"><span className="text-text-primary/[0.4]">Unsettled</span><strong className="text-text-primary/70">3 people</strong></div></div><button type="button" className="mt-6 w-full rounded-2xl bg-success px-4 py-3 text-xs font-black text-text-on-brand">View settlement</button></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex items-center justify-between"><div><p className="text-[10px] font-black uppercase tracking-[0.18em] text-text-primary/[0.36]">Recent activity</p><p className="mt-1 text-lg font-black text-text-primary">Tokyo Drift</p></div><WalletIcon className="h-6 w-6 text-success" /></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="mt-5 space-y-3">{MONEY_ACTIVITY.map(_temp5)}</div>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="grid items-center gap-9 lg:grid-cols-[0.88fr_1.12fr] lg:gap-16">{t3}<div className={`${glassStrong} overflow-hidden rounded-[34px] p-4 sm:p-5`}><div className="grid gap-4 sm:grid-cols-[0.85fr_1.15fr]">{t9}<div className="rounded-[26px] border border-border bg-bg p-5">{t10}{t11}<button type="button" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-surface/60 px-4 py-3 text-xs font-black text-text-primary"><ArrowRightIcon className="h-4 w-4" />Add expense</button></div></div></div></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  return t12;
}
function _temp5(item) {
  return <div key={item.item} className="flex items-center gap-3 rounded-2xl border border-border bg-surface/45 p-3"><Initial tone={item.tone}>{item.who[0]}</Initial><div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-text-primary/[0.84]">{item.item}</p><p className="text-[10px] text-text-primary/[0.36]">Paid by {item.who}</p></div><strong className="text-xs text-success-text">{item.amount}</strong></div>;
}
export function ReceiptShowcase() {
  const $ = _c(15);
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Eyebrow icon={ReceiptIcon}>AI receipt analysis</Eyebrow>;
    t1 = <h2 className="mt-6 max-w-xl text-3xl font-black tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-6xl">Photograph it. Verify it. Split the actual items.</h2>;
    t2 = <p className="mt-5 max-w-xl text-base leading-7 text-text-primary/[0.56] sm:text-lg">Boy Trips reads the merchant, line items, tax, tip, and total. Assign items to people, review the result, and send the expense straight into the trip ledger.</p>;
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div>{t0}{t1}{t2}<div className="mt-8 space-y-3"><CheckRow>Extract line items instead of only reading the total</CheckRow><CheckRow>Handle shared dishes, tax, tip, and custom participants</CheckRow><CheckRow>Keep the original receipt attached for later review</CheckRow></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pointer-events-none absolute inset-x-[8%] top-[18%] h-px bg-gradient-to-r from-transparent via-brand-accent to-transparent shadow-brand-glow" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center justify-between"><div><p className="text-[10px] font-black uppercase tracking-[0.18em] text-text-primary/[0.35]">Receipt scan</p><p className="mt-1 text-lg font-black text-text-primary">Noodle House</p></div><ReceiptIcon className="h-6 w-6 text-brand-accent" /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex justify-between text-text-primary/[0.58]"><span>Ramen × 3</span><span>$51.00</span></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex justify-between text-text-primary/[0.58]"><span>Gyoza × 2</span><span>$18.00</span></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex justify-between text-text-primary/[0.58]"><span>Drinks</span><span>$24.00</span></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="mt-7 space-y-3 border-y border-dashed border-border py-5 text-xs">{t6}{t7}{t8}<div className="flex justify-between text-text-primary/[0.58]"><span>Tax + tip</span><span>$27.00</span></div></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mt-5 flex items-end justify-between"><span className="text-xs font-bold text-text-primary/[0.42]">Detected total</span><span className="text-3xl font-black tracking-[-0.05em] text-text-primary">$120.00</span></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className={`${glassStrong} relative overflow-hidden rounded-[30px] p-5`}>{t4}{t5}{t9}{t10}<div className="mt-5 flex items-center gap-2 rounded-2xl border border-border bg-success-soft p-3 text-xs font-bold text-success-text"><CheckIcon className="h-4 w-4" />9 items matched</div></div>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="flex items-center justify-between"><div><p className="text-[10px] font-black uppercase tracking-[0.18em] text-text-primary/[0.35]">Split preview</p><p className="mt-1 text-lg font-black text-text-primary">Dinner · 7 people</p></div><SplitIcon className="h-6 w-6 text-brand-secondary" /></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mt-5 space-y-2.5">{RECEIPT_SPLITS.map(_temp6)}</div>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="grid items-center gap-9 lg:grid-cols-[0.86fr_1.14fr] lg:gap-16">{t3}<div className="grid gap-4 sm:grid-cols-2">{t11}<div className={`${glass} rounded-[30px] p-5`}>{t12}{t13}<div className="mt-5 rounded-2xl bg-brand-primary p-4 text-text-on-brand"><div className="flex items-center justify-between"><span className="text-xs font-bold opacity-60">Ready to add</span><span className="text-xl font-black">$120.00</span></div><button type="button" className="mt-4 w-full rounded-xl bg-surface px-3 py-2.5 text-xs font-black text-text-primary">Confirm split</button></div></div></div></div>;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  return t14;
}
function _temp6(split) {
  return <div key={split.name} className="flex items-center gap-3 rounded-2xl border border-border bg-surface/45 p-3"><Initial tone={split.tone}>{split.name[0]}</Initial><div className="min-w-0 flex-1"><p className="text-sm font-bold text-text-primary/[0.82]">{split.name}</p><p className="truncate text-[10px] text-text-primary/[0.36]">{split.state}</p></div><strong className="text-sm text-text-primary">{split.amount}</strong></div>;
}
export function MemoriesShowcase() {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-3"><span className="grid h-10 w-10 place-items-center rounded-2xl bg-brand-secondary-soft text-brand-secondary"><ImageIcon className="h-5 w-5" /></span><div><p className="text-sm font-black text-text-primary">Tokyo Drift</p><p className="text-xs text-text-primary/[0.4]">Shared album · 286 items</p></div></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center justify-between border-b border-border pb-4">{t0}<button type="button" className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface/60 px-3 py-2 text-[10px] font-black text-text-primary"><UploadIcon className="h-4 w-4" />Upload</button></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={`${glassStrong} order-2 overflow-hidden rounded-[32px] p-4 sm:p-5 lg:order-1`}>{t1}<div className="mt-4 grid h-[320px] grid-cols-3 grid-rows-3 gap-2 sm:h-[420px] lg:h-[470px]">{MEDIA_TILES.map(_temp7)}</div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Eyebrow icon={CameraIcon}>Shared memories</Eyebrow>;
    t4 = <h2 className="mt-6 max-w-xl text-3xl font-black tracking-[-0.05em] text-text-primary sm:text-5xl lg:text-6xl">The trip ends. The group album does not.</h2>;
    t5 = <p className="mt-5 max-w-xl text-base leading-7 text-text-primary/[0.56] sm:text-lg">Everyone uploads to one shared trip library. Photos, videos, receipts, and favorite moments stay organized by trip and available to the crew.</p>;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="grid items-center gap-9 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">{t2}<div className="order-1 lg:order-2">{t3}{t4}{t5}<div className="mt-8 space-y-3"><CheckRow>One media library for the entire group</CheckRow><CheckRow>Keep originals instead of sending compressed files</CheckRow><CheckRow>Find moments by person, day, or location</CheckRow></div></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}
function _temp7(tile, index) {
  return <div key={index} className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tile.style} ${tile.span}`}><div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgb(255_255_255_/_0.28),transparent_25%)]" />{tile.label && <div className="absolute bottom-4 left-4"><p className="text-[10px] font-black uppercase tracking-[0.18em] text-text-on-brand/75">Night one</p><p className="mt-1 text-lg font-black text-text-on-brand">{tile.label}</p></div>}{index === 3 && <CameraIcon className="absolute bottom-4 right-4 h-6 w-6 text-text-on-brand/80" />}</div>;
}
export function LaunchShowcase(t0) {
  const $ = _c(14);
  const {
    authenticated,
    newGroupHref,
    onSignup,
    onLogin
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,var(--color-brand-primary),transparent_38%)] opacity-[0.18]" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-border bg-brand-primary-soft text-brand-primary shadow-brand-glow"><RouteIcon className="h-7 w-7" /></div>;
    t3 = <h2 className="mt-7 text-3xl font-black tracking-[-0.055em] text-text-primary sm:text-6xl lg:text-7xl">Stop saying “we should.”</h2>;
    t4 = <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-text-primary/[0.55] sm:text-lg">Put the crew in one place and make the next trip real.</p>;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== authenticated || $[5] !== newGroupHref || $[6] !== onSignup) {
    t5 = authenticated ? <Link href={newGroupHref} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 text-sm font-black text-text-on-brand transition hover:-translate-y-0.5 hover:bg-brand-primary-hover">Create another group<ArrowRightIcon className="h-4 w-4" /></Link> : <button type="button" data-overlay-target={true} onClick={onSignup} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 text-sm font-black text-text-on-brand transition hover:-translate-y-0.5 hover:bg-brand-primary-hover">Start a Boy Trip<ArrowRightIcon className="h-4 w-4" /></button>;
    $[4] = authenticated;
    $[5] = newGroupHref;
    $[6] = onSignup;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== authenticated || $[9] !== onLogin) {
    t6 = !authenticated && <button type="button" data-overlay-target={true} onClick={onLogin} className="inline-flex items-center rounded-full border border-border bg-surface/70 px-6 py-3.5 text-sm font-bold text-text-primary transition hover:border-border hover:bg-surface/85">Sign in</button>;
    $[8] = authenticated;
    $[9] = onLogin;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <div className={`${glassStrong} relative overflow-hidden rounded-[36px] px-6 py-14 text-center sm:px-10 sm:py-20`}>{t1}<div className="relative mx-auto max-w-3xl">{t2}{t3}{t4}<div className="mt-8 flex flex-wrap justify-center gap-3">{t5}{t6}</div></div></div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}